import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { volApi } from "./volApi";
import { Calendar, Clock, ArrowUpRight } from "lucide-react";

export default function VolunteerHome() {
  const { profile, can } = useOutletContext();
  const [upcoming, setUpcoming] = useState([]);
  const [hours, setHours] = useState({ rows: [], total_approved: 0, total_pending: 0 });
  const [loading, setLoading] = useState(true);

  const showEvents = can ? can("volunteer.events.view") : true;
  const showHours = can ? can("volunteer.hours.log") : true;

  useEffect(() => {
    const eventsP = showEvents
      ? volApi.get("/volunteers/me/upcoming-events").catch(() => ({ data: [] }))
      : Promise.resolve({ data: [] });
    const hoursP = showHours
      ? volApi.get("/volunteers/me/hours").catch(() => ({ data: { rows: [], total_approved: 0, total_pending: 0 } }))
      : Promise.resolve({ data: { rows: [], total_approved: 0, total_pending: 0 } });
    Promise.all([eventsP, hoursP])
      .then(([e, h]) => {
        setUpcoming(e.data || []);
        setHours(h.data || {});
      })
      .finally(() => setLoading(false));
  }, [showEvents, showHours]);

  const nextEvent = upcoming.filter((e) => e.i_have_rsvped)[0] || upcoming[0];

  return (
    <div className="space-y-8" data-testid="volunteer-home">
      <div>
        <div className="hw-eyebrow mb-2">Welcome back</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">
          Hi, {profile?.name?.split(" ")[0] || "friend"} 👋
        </h1>
        <p className="text-[#334155] mt-2 max-w-2xl">
          Thank you for showing up for Eastern North Carolina. Here&apos;s what&apos;s on your plate.
        </p>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white border border-[#E5E5E0] rounded-2xl h-40 animate-pulse" />
          <div className="bg-white border border-[#E5E5E0] rounded-2xl h-40 animate-pulse" />
        </div>
      ) : (!showEvents && !showHours) ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="volunteer-home-no-access">
          <p className="text-[#334155]">
            Your current volunteer role doesn&apos;t have any portal features enabled yet.
            <br />Reach out to your HarborWell contact if you think this is a mistake.
          </p>
        </div>
      ) : (
        <div className={`grid ${showEvents && showHours ? "sm:grid-cols-2" : "sm:grid-cols-1"} gap-4`}>
          {/* Next event */}
          {showEvents && (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5" data-testid="volunteer-home-next-event">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center">
                <Calendar className="w-4 h-4" strokeWidth={1.75} />
              </div>
              <div>
                <div className="hw-eyebrow text-[#1E3A8A]">Next up</div>
                <div className="text-sm font-medium text-[#0F172A]">
                  {nextEvent?.i_have_rsvped ? "Your next RSVP" : "Closest upcoming event"}
                </div>
              </div>
            </div>
            {nextEvent ? (
              <div>
                <div className="font-display text-lg font-semibold text-[#0F172A]">{nextEvent.title}</div>
                <div className="text-sm text-[#334155] mt-1">
                  {nextEvent.activity_date}
                  {nextEvent.county && ` · ${nextEvent.county} County`}
                  {nextEvent.location && ` · ${nextEvent.location}`}
                </div>
                <Link
                  to="/volunteer/events"
                  data-testid="volunteer-home-go-events"
                  className="mt-3 inline-flex items-center gap-1 text-sm text-[#1E3A8A] font-medium hover:underline"
                >
                  See all events <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ) : (
              <p className="text-sm text-[#334155]">
                No upcoming events on the calendar yet — check back soon.
              </p>
            )}
          </div>
          )}

          {/* Hours summary */}
          {showHours && (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5" data-testid="volunteer-home-hours">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-[#7E937D] text-white flex items-center justify-center">
                <Clock className="w-4 h-4" strokeWidth={1.75} />
              </div>
              <div>
                <div className="hw-eyebrow text-[#1E3A8A]">Your impact</div>
                <div className="text-sm font-medium text-[#0F172A]">Hours logged with HarborWell</div>
              </div>
            </div>
            <div className="flex items-baseline gap-4">
              <div>
                <div className="text-3xl font-display font-semibold text-[#0F172A]" data-testid="volunteer-home-hours-approved">
                  {hours.total_approved?.toFixed(2) || "0.00"}
                </div>
                <div className="text-xs text-[#1E3A8A]">approved</div>
              </div>
              {(hours.total_pending || 0) > 0 && (
                <div>
                  <div className="text-xl font-display font-semibold text-[#92400E]" data-testid="volunteer-home-hours-pending">
                    {hours.total_pending.toFixed(2)}
                  </div>
                  <div className="text-xs text-[#92400E]">pending</div>
                </div>
              )}
            </div>
            <Link
              to="/volunteer/hours"
              data-testid="volunteer-home-go-hours"
              className="mt-4 inline-flex items-center gap-1 text-sm text-[#1E3A8A] font-medium hover:underline"
            >
              Log hours <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          )}
        </div>
      )}
    </div>
  );
}
